// Desktop source manager (sources-v2 D1, D2, D6): every live camera/screen stream by source id, refcounted, with an armed set that keeps a source live off stage. Mobile and /camera keep `getCamera`.

import { isFileSource, LEGACY_FILE } from './ids'

export const ARMED_KEY = 'oylo-armed-sources'
const MAX_RECOVERY = 3
const RECOVERY_RESET_MS = 10000

/** 'screen' | 'camera' for a stream source; null for one the stage shows without acquiring ('none', a file, a shader's parked camera). */
export const kindOf = id => {
	if (!id || id === 'none' || id === LEGACY_FILE || isFileSource(id)) return null
	return id === 'screen' ? 'screen' : 'camera'
}

const stopStream = stream => {
	for (const t of stream?.getTracks() ?? []) t.stop()
}

/**
 * @param {object} deps
 * @param {(id: string) => Promise<{stream?: MediaStream, error?: {name: string}}>} deps.open - acquires a source without touching the stage
 * @param {(stream: MediaStream|null, id: string) => void} deps.show - publishes the on-stage source's stream (the D2 bridge)
 * @param {(id: string, error: object|null) => void} deps.lost - the on-stage source failed to open or died; error null = the user stopped a screen share
 * @param {(loading: boolean) => void} [deps.loading]
 * @param {Storage} [deps.storage] - the armed set, per machine
 */
export function createSourceManager({
	open: opener,
	show,
	lost,
	loading = () => {},
	storage = globalThis.localStorage,
}) {
	const logger = console.scope('sources')
	const entries = new Map()
	const listeners = new Set()
	let snap = null
	let onStage = null
	let stageGen = 0

	const readArmed = () => {
		try {
			return new Set(JSON.parse(storage?.getItem(ARMED_KEY) || '[]').filter(kindOf))
		} catch {
			return new Set()
		}
	}
	const armedIds = readArmed()
	const persist = () => {
		try {
			storage?.setItem(ARMED_KEY, JSON.stringify([...armedIds]))
		} catch (e) {
			logger.warn('could not persist armed sources', e)
		}
	}

	const notify = () => {
		snap = null
		for (const fn of listeners) fn()
	}

	const entry = id => {
		let e = entries.get(id)
		if (!e) {
			e = {
				id,
				kind: kindOf(id),
				refs: 0,
				armed: false,
				needsGesture: false,
				state: 'off',
				stream: null,
				pending: null,
				error: null,
				attempts: 0,
				stableTimer: null,
				onEnded: null,
			}
			entries.set(id, e)
		}
		return e
	}

	const wanted = e => e.refs > 0 || e.armed

	const detach = e => {
		clearTimeout(e.stableTimer)
		const stream = e.stream
		e.stream = null
		for (const t of stream?.getTracks() ?? []) t.removeEventListener('ended', e.onEnded)
		return stream
	}

	const close = e => {
		stopStream(detach(e))
		e.state = 'off'
		e.attempts = 0
		notify()
	}

	const setArmed = (e, armed) => {
		e.armed = armed
		e.needsGesture = false
		if (armed) armedIds.add(e.id)
		else armedIds.delete(e.id)
		persist()
	}

	const attach = (e, stream) => {
		e.stream = stream
		e.state = 'ready'
		e.onEnded = () => ended(e)
		for (const t of stream.getTracks()) t.addEventListener('ended', e.onEnded)
		clearTimeout(e.stableTimer)
		e.stableTimer = setTimeout(() => {
			e.attempts = 0
		}, RECOVERY_RESET_MS)
		if (onStage === e.id) show(stream, e.id)
		notify()
	}

	const open = e => {
		if (e.stream) return Promise.resolve(e.stream)
		e.state = 'arming'
		if (e.pending) return e.pending
		e.error = null
		e.needsGesture = false
		notify()
		const p = opener(e.id).then(({ stream, error }) => {
			e.pending = null
			if (error) {
				logger.warn('open failed', e.id, error.name)
				e.error = error
				if (e.kind === 'screen' && e.armed) setArmed(e, false)
				e.state = wanted(e) ? 'lost' : 'off'
				notify()
				return null
			}
			if (!wanted(e)) {
				stopStream(stream)
				e.state = 'off'
				notify()
				return null
			}
			attach(e, stream)
			return stream
		})
		e.pending = p
		return p
	}

	// A track ended that nobody stopped: a screen share the user ended, or a camera unplugged/seized. `track.stop()` never fires `ended`.
	function ended(e) {
		stopStream(detach(e))
		const staged = onStage === e.id
		if (staged) show(null, e.id)
		if (e.kind === 'screen') {
			logger.info('screen share ended by user')
			if (e.armed) setArmed(e, false)
			e.state = 'off'
			notify()
			if (staged) lost(e.id, null)
			return
		}
		if (!wanted(e)) return close(e)
		if (e.attempts >= MAX_RECOVERY) {
			logger.error(`${e.id} recovery failed after ${MAX_RECOVERY} attempts`)
			e.attempts = 0
			e.state = 'lost'
			notify()
			if (staged) lost(e.id, e.error ?? { name: 'Ended' })
			return
		}
		e.attempts++
		logger.warn(`${e.id} ended unexpectedly, recovering (${e.attempts}/${MAX_RECOVERY})`)
		open(e).then(stream => {
			if (!stream && onStage === e.id) lost(e.id, e.error)
		})
	}

	/** Takes a ref and opens the source if needed. Resolves to its stream, or null when it failed or was released meanwhile. */
	const acquire = id => {
		const e = entry(id)
		e.refs++
		return open(e)
	}

	/** Drops a ref; the source stops at zero refs unless armed. */
	const release = id => {
		const e = entries.get(id)
		if (!e) return
		e.refs = Math.max(0, e.refs - 1)
		if (!wanted(e)) close(e)
	}

	/** Keeps a camera or screen live off stage; anything else is refused. A screen needs a user gesture on every arm. */
	const arm = id => {
		if (!kindOf(id)) return Promise.resolve(null)
		const e = entry(id)
		setArmed(e, true)
		if (e.stream) notify()
		return open(e)
	}

	const disarm = id => {
		const e = entry(id)
		setArmed(e, false)
		if (!wanted(e)) close(e)
		else notify()
	}

	/** Puts a source on stage: the new one is acquired and published BEFORE the old ref is released, so a switch between live sources never blanks. Superseded calls release what they took. */
	const stage = async id => {
		const gen = ++stageGen
		const prev = onStage
		let stream = null
		loading(!!kindOf(id))
		if (kindOf(id)) {
			stream = await acquire(id)
			if (gen !== stageGen) {
				release(id)
				return
			}
			loading(false)
			if (!stream) {
				release(id)
				lost(id, entries.get(id)?.error ?? null)
				return
			}
		}
		onStage = id
		if (prev !== id || entries.get(id)?.stream !== stream) show(stream, id)
		if (kindOf(prev)) release(prev)
		notify()
	}

	/** Re-arms the persisted cameras present in `deviceIds` (no gesture needed), and reopens an armed camera that was lost and is back. */
	const restore = deviceIds => {
		for (const id of deviceIds) {
			if (kindOf(id) !== 'camera' || !armedIds.has(id)) continue
			const e = entry(id)
			if (!e.armed) arm(id)
			else if (e.state === 'lost') open(e)
		}
	}

	// Ids an older build armed ('none', 'video_file') are dropped from storage too.
	persist()

	// An armed screen survives a reload only as a reminder: getDisplayMedia needs a gesture every time.
	if (armedIds.has('screen')) entry('screen').needsGesture = true

	const snapshot = () => {
		if (!snap) {
			const sources = {}
			for (const e of entries.values())
				sources[e.id] = { state: e.state, armed: e.armed, needsGesture: e.needsGesture, refs: e.refs }
			snap = { onStage, sources }
		}
		return snap
	}

	const dispose = () => {
		stageGen++
		for (const e of entries.values()) stopStream(detach(e))
		entries.clear()
		onStage = null
	}

	return {
		acquire,
		release,
		arm,
		disarm,
		stage,
		restore,
		dispose,
		snapshot,
		state: id => entries.get(id)?.state ?? 'off',
		stream: id => entries.get(id)?.stream ?? null,
		/** @returns {() => void} unsubscribe */
		subscribe: fn => {
			listeners.add(fn)
			return () => listeners.delete(fn)
		},
	}
}
